import React, { useEffect, useState } from 'react';
import { validate as validateEmail } from 'email-validator';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { extractDigits, formatPartialPhoneNumber, FormTableFieldType } from '@tempus/patient-forms-service-shared';
import FormTable, { FormTableField } from './FormTable';
import { REQUIRED_FIELD_WARNING } from './validation-utils';
import Form from './Form';
import { Button } from '@tcl-boron-prefabs/button';
import { useForm, useFormState } from 'react-final-form';
import config from '../../../../config';
import classNames from 'classnames';
import fetchHelper from '../../../utils';
import { AuthenticationType } from '../../../api/AuthenticationType';
import { PortalViewsEnum } from '@tempus/portal-service-shared';
import Loader from '@tcl-boron-prefabs/loader';
import { jwtDecode } from 'jwt-decode';
import { storage } from './utils';
import { Fail } from '@tcl-boron-icons/icons';
import { ErrorPalette } from '@tcl-boron-colors/colors';
import standaloneStyles from './standalone-styles.module.css';

const SESSION_STORAGE_KEY = 'patient-lookup-response';

export const EXCLUDED_FORM_FIELDS = [
  'patientFirstName',
  'patientLastName',
  'patientDateOfBirth',
  'patientPhoneNumber',
  'patientEmail',
];

export type PatientStandaloneFormAuthenticationResponse = {
  identityVerificationToken: string;
  referenceId?: string;
  patientId: string;
};

export type PatientStandaloneFormAuthenticationFormValues = {
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientPhoneNumber: string;
  patientEmail: string;
};

export type PatientStandaloneFormAuthenticationToken = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phoneNumber: string;
  patientId: string;
  referenceId?: string;
  exp: number;
};

export type PatientStandaloneFormAuthenticationValues = PatientStandaloneFormAuthenticationResponse &
  PatientStandaloneFormAuthenticationFormValues;

interface PatientStandaloneFormAuthenticationManagerProps {
  onAuthenticationSuccess: (data: PatientStandaloneFormAuthenticationValues) => void;
  onAuthenticationError: () => void;
  onSendTokenizedLinkDispatched: () => void;
  onSendTokenizedLinkError: () => void;
  onClear: () => void;
  formType: string;
}

interface PatientStandaloneFormAuthenticationFormProps extends PatientStandaloneFormAuthenticationManagerProps {
  disableInputs: boolean;
  defaultValues?: PatientStandaloneFormAuthenticationFormValues;
}

export const StandaloneFormAuthenticationError: React.FC = () => {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className={standaloneStyles.statusContainer}>
        <Fail color={ErrorPalette[700]} />
        <p className={standaloneStyles.subtitle} style={{ margin: 0 }}>
          <strong>Invalid or Expired Link</strong>
        </p>
      </div>
      <p className={standaloneStyles.subtitle}>
        There was an error authenticating your one-time link. This may be due to an invalid or expired link.
      </p>
      <p className={standaloneStyles.subtitle}>
        If this issue persists, please contact the Tempus Customer Success team at{' '}
        <a href="mailto:help@tempus.com">help@tempus.com</a> or call <a href="tel:312-598-9961">312-598-9961</a>.
      </p>
    </div>
  );
};

const PatientStandaloneFormAuthenticationForm: React.FC<PatientStandaloneFormAuthenticationFormProps> = (props) => {
  const form = useForm();
  const formState = useFormState();

  const resetForm = () => {
    form.restart();
    props.onClear();
  };

  const patientLookupFormFields: FormTableField[] = [
    {
      name: 'patientFirstName',
      label: 'Patient First Name',
      type: FormTableFieldType.TEXT_INPUT,
      props: {
        placeholder: "Enter patient's first name",
        initialValue: props.defaultValues?.patientFirstName,
        readOnly: props.disableInputs,
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
    },
    {
      name: 'patientLastName',
      label: 'Patient Last Name',
      type: FormTableFieldType.TEXT_INPUT,
      props: {
        placeholder: "Enter patient's last name",
        initialValue: props.defaultValues?.patientLastName,
        readOnly: props.disableInputs,
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
    },
    {
      name: 'patientDateOfBirth',
      label: 'Patient Date of Birth',
      type: FormTableFieldType.DATE_PICKER,
      props: {
        placeholder: 'MM/DD/YYYY',
        initialValue: props.defaultValues?.patientDateOfBirth,
        readOnly: props.disableInputs,
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
      },
    },
    {
      name: 'patientEmail',
      label: 'Patient Email',
      type: FormTableFieldType.TEXT_INPUT,
      props: {
        placeholder: 'patient@example.com',
        initialValue: props.defaultValues?.patientEmail,
        readOnly: props.disableInputs,
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
        if (!validateEmail(value)) {
          return 'Please enter a valid email address';
        }
      },
    },
    {
      name: 'patientPhoneNumber',
      label: 'Patient Phone Number (USA Only)',
      type: FormTableFieldType.US_PHONE_INPUT,
      props: {
        placeholder: '(888) 888-8888',
        initialValue: props.defaultValues?.patientPhoneNumber,
        readOnly: props.disableInputs,
      },
      validate: (value: any | undefined) => {
        if (!value) return REQUIRED_FIELD_WARNING;
        if (!isValidPhoneNumber(value, 'US')) {
          return 'Please enter a valid phone number';
        }
      },
    },
  ];

  return (
    <div data-testid="patient-lookup-form">
      <FormTable fields={patientLookupFormFields} />
      <div className={classNames(standaloneStyles.footer, standaloneStyles.buttonGroup)}>
        <Button
          buttonType="secondary"
          loading={formState.submitting}
          ariaLabel="patient-lookup-form-clear"
          type="reset"
          onClick={resetForm}
        >
          Reset
        </Button>
        <Button
          loading={formState.submitting}
          disabled={props.disableInputs}
          ariaLabel="patient-lookup-form-submit"
          type="submit"
        >
          Get One-Time Link
        </Button>
      </div>
    </div>
  );
};

const PatientStandaloneFormAuthenticationManager: React.FC<PatientStandaloneFormAuthenticationManagerProps> = (
  props,
) => {
  const [initialValues, setInitialValues] = useState<PatientStandaloneFormAuthenticationFormValues | undefined>();

  const [isLoading, setIsLoading] = useState(true);
  const [areInputsDisabled, setAreInputsDisabled] = useState(false);

  const clearTokenFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('token')) {
      urlParams.delete('token');
      const newUrl = `${window.location.origin}${window.location.pathname}${
        urlParams.toString() ? `?${urlParams.toString()}` : ''
      }`;
      // Use replaceState to avoid adding to browser history
      window.history.replaceState({}, document.title, newUrl);
    }
  };

  const tryToLoadIdToken = async () => {
    setIsLoading(true);

    const isIdTokenValid = (token: string) => {
      if (token && token.length > 0) {
        try {
          const decodedToken = jwtDecode<PatientStandaloneFormAuthenticationToken>(token);

          const hasAllMetadataFields =
            decodedToken.firstName &&
            decodedToken.lastName &&
            decodedToken.dateOfBirth &&
            decodedToken.email &&
            decodedToken.phoneNumber &&
            decodedToken.patientId;

          const now = Math.floor(Date.now() / 1000);
          if (decodedToken && decodedToken.exp && decodedToken.exp > now && hasAllMetadataFields) {
            return true;
          } else {
            return false;
          }
        } catch (e) {
          return false;
        }
      } else {
        return false;
      }
    };

    const onSuccessfulIdTokenLoad = (idToken: string) => {
      const decodedToken = jwtDecode<PatientStandaloneFormAuthenticationToken>(idToken);

      const initialValues = {
        patientFirstName: decodedToken.firstName,
        patientLastName: decodedToken.lastName,
        patientDateOfBirth: decodedToken.dateOfBirth,
        patientEmail: decodedToken.email,
        patientPhoneNumber: formatPartialPhoneNumber(extractDigits(decodedToken.phoneNumber).slice(-10)),
        referenceId: decodedToken.referenceId,
      };
      setInitialValues(initialValues);

      const allAuthenticationValues: PatientStandaloneFormAuthenticationValues = {
        ...initialValues,
        patientId: decodedToken.patientId,
        referenceId: decodedToken.referenceId,
        identityVerificationToken: idToken,
      };

      props.onAuthenticationSuccess(allAuthenticationValues);
      storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(allAuthenticationValues));
      setAreInputsDisabled(true);
      setIsLoading(false);
    };

    const sessionData = storage.getItem(SESSION_STORAGE_KEY);
    if (sessionData) {
      try {
        const parsedData: PatientStandaloneFormAuthenticationValues = JSON.parse(sessionData);
        if (isIdTokenValid(parsedData.identityVerificationToken)) {
          onSuccessfulIdTokenLoad(parsedData.identityVerificationToken);
          return;
        } else {
          storage.removeItem(SESSION_STORAGE_KEY);
        }
      } catch (e) {
        storage.removeItem(SESSION_STORAGE_KEY);
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      try {
        const idToken = await fetchHelper<string>(`/form/standalone/exchange-token`, {
          authenticationType: AuthenticationType.NONE,
          baseUrl: config.patientWebServiceBaseUrl,
          method: 'POST',
          body: {
            token: tokenParam,
          },
          headerOverrides: {
            'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
          },
        });
        if (isIdTokenValid(idToken)) {
          onSuccessfulIdTokenLoad(idToken);
        } else {
          props.onAuthenticationError();
        }
      } catch (error: any) {
        props.onAuthenticationError();
      } finally {
        setIsLoading(false);
        clearTokenFromUrl();
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    tryToLoadIdToken();
  }, []);

  const onPatientLookupFormSubmit = async (values: any) => {
    try {
      const { patientFirstName, patientLastName, patientDateOfBirth, patientEmail, patientPhoneNumber } = values;
      await fetchHelper<PatientStandaloneFormAuthenticationResponse>(`/form/standalone/send-tokenized-link`, {
        authenticationType: AuthenticationType.NONE,
        baseUrl: config.patientWebServiceBaseUrl,
        method: 'POST',
        body: {
          formType: props.formType,
          firstName: patientFirstName,
          lastName: patientLastName,
          dateOfBirth: patientDateOfBirth,
          email: patientEmail,
          phoneNumber: extractDigits(patientPhoneNumber),
        },
        headerOverrides: {
          'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
        },
      });
      props.onSendTokenizedLinkDispatched();
      setAreInputsDisabled(true);
    } catch (error) {
      props.onSendTokenizedLinkError();
      setAreInputsDisabled(false);
    }
  };

  if (isLoading) {
    return (
      <div className={standaloneStyles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <Form onSubmit={onPatientLookupFormSubmit}>
      <PatientStandaloneFormAuthenticationForm
        {...props}
        onClear={() => {
          storage.removeItem(SESSION_STORAGE_KEY);
          props.onClear();
          setAreInputsDisabled(false);
          setInitialValues(undefined);
          setIsLoading(false);
        }}
        disableInputs={areInputsDisabled}
        defaultValues={initialValues}
      />
    </Form>
  );
};

export default PatientStandaloneFormAuthenticationManager;
